import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// localStorage keys for persisted sidebar UI state.
const STORAGE_WIDTH = 'ccc.sidebar.width';
const STORAGE_SECTIONS = 'ccc.sidebar.sections';

export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 420;
export const SIDEBAR_DEFAULT_WIDTH = 264;

// Mobile breakpoint (matches Tailwind's md). Below this the sidebar becomes an overlay.
const MOBILE_BREAKPOINT = 768;

type SectionState = Record<string, boolean>;

interface SidebarContextValue {
  // Desktop width (px), resizable and persisted.
  width: number;
  setWidth: (width: number) => void;

  // Whether the viewport is in mobile mode (overlay sidebar).
  isMobile: boolean;

  // Mobile overlay open/closed state.
  mobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
  toggleMobile: () => void;

  // Collapsible section state (keyed by section id). `defaultOpen` controls the
  // open/closed state when the section has no persisted value yet (defaults to
  // expanded to preserve existing section behavior).
  isSectionOpen: (id: string, defaultOpen?: boolean) => boolean;
  toggleSection: (id: string, defaultOpen?: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

function readWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(STORAGE_WIDTH);
  const parsed = raw ? Number(raw) : NaN;
  if (Number.isFinite(parsed)) {
    return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed));
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

function readSections(): SectionState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_SECTIONS);
    return raw ? (JSON.parse(raw) as SectionState) : {};
  } catch {
    return {};
  }
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [width, setWidthState] = useState<number>(readWidth);
  const [sections, setSections] = useState<SectionState>(readSections);
  const [isMobile, setIsMobile] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  // Track viewport size to switch between desktop/mobile sidebar modes.
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Always close the mobile overlay when growing back to desktop.
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setWidth = useCallback((next: number) => {
    const clamped = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, next));
    setWidthState(clamped);
    window.localStorage.setItem(STORAGE_WIDTH, String(clamped));
  }, []);

  const toggleSection = useCallback((id: string, defaultOpen = true) => {
    setSections((prev) => {
      // Absent key falls back to the section's default open state, so the first
      // toggle flips from that default.
      const current = prev[id] ?? defaultOpen;
      const next = { ...prev, [id]: !current };
      window.localStorage.setItem(STORAGE_SECTIONS, JSON.stringify(next));
      return next;
    });
  }, []);

  const isSectionOpen = useCallback(
    (id: string, defaultOpen = true) => sections[id] ?? defaultOpen,
    [sections],
  );

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);

  const value = useMemo<SidebarContextValue>(
    () => ({
      width,
      setWidth,
      isMobile,
      mobileOpen,
      openMobile,
      closeMobile,
      toggleMobile,
      isSectionOpen,
      toggleSection,
    }),
    [
      width,
      setWidth,
      isMobile,
      mobileOpen,
      openMobile,
      closeMobile,
      toggleMobile,
      isSectionOpen,
      toggleSection,
    ],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return ctx;
}
